/* 211 §10 — A MATTER PAGE'S OWN LIST, ASKED THE 210 §1 WAY.

   docs/211-deploy-note.md §8.5 (the census): five reads a matter page makes
   itself answered `[]` when they failed, and the page then said « 0 parties »,
   « No templates … », or counted its timeline short. The
   rule is 210 §1's (src/lib/listLoad.ts): a read that fails is asked again,
   twice, a second apart, each failure on the console — `loadLists`, as it
   stands. Still failing, nothing takes its place: `failure` is set and the
   rows stay empty, and the host — which layers this list over App's marks
   (`usePageListContext`) — draws `ListNotLoaded` where the list is shown,
   counted or picked from. « Réessayer » asks this list alone; so does the
   window coming back, while it is not loaded (App's focus cycle, for the boot's).

   `path` is what is asked (`null`: nothing — no rows, no failure), `refresh`
   asks it again without forgetting what it holds (the timeline re-reads its
   chronicle as entries come and go), and `fetch` must be STABLE — a module-scope
   function taking the path — or every render would ask again. */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { loadLists, layerPageLists, ListLoadContext } from "../lib/listLoad";
import type { ListFailure, ListLoadState, PageListEntry } from "../lib/listLoad";

/** The stable `fetch` for a read that answers the list itself: the path, asked as it is. */
export const readList = (path: string): Promise<unknown> => api(path);

/** The rows of a list with none: one array, so a host's memo sees the same one every render. */
const NONE: readonly never[] = Object.freeze([]);

/** What an ask left, and for which path and which ask. */
interface Answered<T> {
  path: string;
  token: string;
  rows: T[];
  failure: ListFailure | null;
}

export interface PageList<T> {
  /** The list's rows — empty until they come, and while it is not loaded. */
  rows: T[];
  /** The list as its host layers it (`usePageListContext`). */
  entry: PageListEntry;
}

export function usePageList<T>(
  key: string,
  name: string,
  path: string | null,
  fetch: typeof readList, // its type reused: a written `(path: string) => …` is a named parameter to base `no-unused-vars` (emailPdfLink.ts)
  refresh = "",
): PageList<T> {
  const [answered, setAnswered] = useState<Answered<T> | null>(null);
  const [nonce, setNonce] = useState(0);
  const token = path == null ? "" : `${path}\n${refresh}\n${String(nonce)}`;
  useEffect(() => {
    if (path == null) return undefined;
    let live = true;
    void loadLists([{ key, name, fetch: () => fetch(path) }]).then((answers) => {
      const a = answers.get(key);
      if (!live || !a) return;
      setAnswered(a.ok ? { path, token, rows: a.rows as T[], failure: null } : { path, token, rows: [], failure: a.failure });
    });
    return () => {
      live = false;
    };
  }, [key, name, path, token, fetch]);
  // what the page shows: this path's last answer — a refresh keeps it until the next one comes
  const mine = path !== null && answered?.path === path ? answered : null;
  const failure = mine ? mine.failure : null;
  const asking = path !== null && mine?.token !== token;
  // not loaded: asked again when the window comes back
  useEffect(() => {
    if (!failure) return undefined;
    const again = () => {
      setNonce((n) => n + 1);
    };
    globalThis.addEventListener("focus", again);
    return () => {
      globalThis.removeEventListener("focus", again);
    };
  }, [failure]);
  const entry = useMemo<PageListEntry>(
    () => ({
      key,
      failure,
      asking,
      retry: () => {
        setNonce((n) => n + 1);
      },
    }),
    [key, failure, asking],
  );
  return { rows: mine ? mine.rows : (NONE as unknown as T[]), entry };
}

/** App's list context with the host's own lists over it — for `ListLoadContext.Provider`. */
export function usePageListContext(first: PageListEntry, second?: PageListEntry | null, third?: PageListEntry | null): ListLoadState {
  const outer = useContext(ListLoadContext);
  return useMemo(() => layerPageLists(outer, [first, second, third]), [outer, first, second, third]);
}

/* 212 §22 — A WINDOW'S OR A SECTION'S OWN READ, THE SAME RULE, ITS HOST KEEPING
   ITS OWN STATE. docs/211-deploy-note.md §8.5 (« Surfaced, not §10's »): seven windows
   answered `[]` when a read failed, two matter sections drew nothing, and a
   failed Microsoft 365 status read « not connected ». Their hosts keep state a
   list hook would not — a checklist edited in place, sources whose ticks are
   seeded in the same commit as their list, a CDD row patched field by field, a
   calculation autosaved — so each asks its read itself, through `ask` below
   (`loadLists` as it stands: asked again, twice, a second apart, each failure
   on the console), and handles the answer as it always did. This hook holds
   what the LAST ask left: the mark `ListNotLoaded` reads (the host layers
   `entry` over App's with `usePageListContext`), `again` — bumped by
   « Réessayer », and by the window coming back while the read is not loaded —
   for the host's effect deps. An ask overtaken by a newer one leaves the mark
   alone. */
export function useReadMark(key: string) {
  const [failure, setFailure] = useState<ListFailure | null>(null);
  const [busy, setBusy] = useState(0);
  const [again, setAgain] = useState(0);
  const latest = useRef(0);
  useEffect(() => {
    if (!failure) return undefined;
    const onFocus = () => {
      setAgain((n) => n + 1);
    };
    globalThis.addEventListener("focus", onFocus);
    return () => {
      globalThis.removeEventListener("focus", onFocus);
    };
  }, [failure]);
  const ask = useCallback(
    async (name: string, fetch: () => Promise<unknown>): Promise<unknown[] | null> => {
      const mine = ++latest.current;
      setBusy((n) => n + 1);
      const a = (await loadLists([{ key, name, fetch }])).get(key);
      setBusy((n) => n - 1);
      if (mine === latest.current) setFailure(a?.ok === false ? a.failure : null);
      return a?.ok ? a.rows : null;
    },
    [key],
  );
  const entry = useMemo<PageListEntry>(
    () => ({
      key,
      failure,
      asking: busy > 0,
      retry: () => {
        setAgain((n) => n + 1);
      },
    }),
    [key, failure, busy],
  );
  return { entry, again, ask };
}

/** A read that answers one object, asked as a one-row list: the rule takes an answer that is not a list for a failure. */
export const oneRow = (p: Promise<unknown>): Promise<unknown[]> => p.then((d) => [d]);
